import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Avatar,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import SchoolIcon from '@mui/icons-material/School';
import GroupsIcon from '@mui/icons-material/Groups';
import TranslateIcon from '@mui/icons-material/Translate';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SectionTitle from '../components/common/SectionTitle';
import { courseGroups } from '../data/courses';
import { testimonials } from '../data/testimonials';

const highlights = [
  {
    icon: <SchoolIcon sx={{ fontSize: 40 }} />,
    title: 'Experienced Tutor',
    description: '20+ years of dedicated Chinese language teaching experience since 2003.',
  },
  {
    icon: <GroupsIcon sx={{ fontSize: 40 }} />,
    title: 'Small Class Size',
    description: 'Personalised attention in small groups for effective learning outcomes.',
  },
  {
    icon: <TranslateIcon sx={{ fontSize: 40 }} />,
    title: 'Comprehensive Curriculum',
    description: 'Covering listening, reading, writing, oral, and exam preparation skills.',
  },
  {
    icon: <EmojiEventsIcon sx={{ fontSize: 40 }} />,
    title: 'Proven Results',
    description: 'Students consistently achieve excellent grades in school exams and PSLE.',
  },
];

const HomePage = () => {
  return (
    <Box>
      {/* Hero Banner */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #FF6F00 0%, #FF8F00 30%, #FFA726 60%, #FFB74D 100%)',
          minHeight: { xs: '70vh', md: '80vh' },
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '-50%',
            right: '-20%',
            width: '600px',
            height: '600px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.1)',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: '-30%',
            left: '-10%',
            width: '400px',
            height: '400px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.08)',
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container alignItems="center" spacing={4}>
            <Grid item xs={12} md={7}>
              <Typography
                variant="h1"
                sx={{
                  color: '#fff',
                  fontWeight: 800,
                  fontSize: { xs: '2.5rem', sm: '3.2rem', md: '3.8rem' },
                  lineHeight: 1.2,
                  mb: 2,
                  textShadow: '0 2px 20px rgba(0,0,0,0.15)',
                }}
              >
                Xing Fan Learning Centre
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  color: 'rgba(255,255,255,0.9)',
                  fontWeight: 400,
                  fontSize: { xs: '1.3rem', md: '1.8rem' },
                  mb: 1,
                }}
              >
                星帆补习中心
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: 'rgba(255,255,255,0.85)',
                  fontWeight: 400,
                  fontSize: { xs: '1rem', md: '1.2rem' },
                  mb: 4,
                  maxWidth: 500,
                  lineHeight: 1.6,
                }}
              >
                Quality Mandarin tuition for K2 to Secondary 4 students. Building confidence in Chinese language since 2003.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  component={RouterLink}
                  to="/courses"
                  sx={{
                    bgcolor: '#fff',
                    color: 'primary.main',
                    fontWeight: 700,
                    px: 4,
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.9)',
                      transform: 'translateY(-2px)',
                    },
                    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                  }}
                >
                  View Courses
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  component={RouterLink}
                  to="/contact"
                  sx={{
                    borderColor: '#fff',
                    color: '#fff',
                    fontWeight: 700,
                    px: 4,
                    '&:hover': {
                      borderColor: '#fff',
                      bgcolor: 'rgba(255,255,255,0.15)',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  Contact Us
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={5} sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'center' }}>
              <Avatar
                src={process.env.PUBLIC_URL + '/images/company-logo.png'}
                alt="Xing Fan Learning Centre"
                sx={{
                  width: 280,
                  height: 280,
                  border: '6px solid rgba(255,255,255,0.3)',
                  boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
                  bgcolor: 'rgba(255,255,255,0.15)',
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Why Choose Us */}
      <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <SectionTitle
            title="Why Choose Us?"
            subtitle="Trusted by parents across Sembawang and beyond"
          />
          <Grid container spacing={3}>
            {highlights.map((item, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  sx={{
                    textAlign: 'center',
                    p: 3,
                    height: '100%',
                    border: '1px solid rgba(0,0,0,0.06)',
                  }}
                >
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      bgcolor: 'primary.light',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2,
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {item.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Course Preview */}
      <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: '#fff' }}>
        <Container maxWidth="lg">
          <SectionTitle
            title="Our Courses"
            subtitle="Comprehensive Chinese language programmes for every level"
          />
          <Grid container spacing={3}>
            {courseGroups.map((course) => (
              <Grid item xs={12} sm={6} md={3} key={course.id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardMedia
                    component="img"
                    height="160"
                    image={course.image}
                    alt={course.titleEn}
                    sx={{ objectFit: 'cover' }}
                  />
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {course.titleEn}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                      {course.titleZh}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                      {course.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Button
              variant="contained"
              component={RouterLink}
              to="/courses"
              endIcon={<ArrowForwardIcon />}
            >
              View All Courses
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Testimonials Preview */}
      <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <SectionTitle
            title="What Parents Say"
            subtitle="Hear from our students and their families"
          />
          <Grid container spacing={2} justifyContent="center">
            {testimonials.slice(0, 4).map((t) => (
              <Grid item xs={6} sm={4} md={3} key={t.id}>
                <Card sx={{ overflow: 'hidden' }}>
                  <CardMedia
                    component="img"
                    image={t.image}
                    alt={`Testimonial ${t.id}`}
                    sx={{ height: 200, objectFit: 'cover' }}
                  />
                </Card>
              </Grid>
            ))}
          </Grid>
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Button
              variant="outlined"
              component={RouterLink}
              to="/testimonials"
              endIcon={<ArrowForwardIcon />}
            >
              See All Testimonials
            </Button>
          </Box>
        </Container>
      </Box>

      {/* CTA Banner */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #26A69A 0%, #00897B 100%)',
          py: { xs: 6, md: 8 },
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h3"
            sx={{
              color: '#fff',
              fontWeight: 700,
              mb: 2,
              fontSize: { xs: '1.8rem', md: '2.5rem' },
            }}
          >
            Ready to Start Your Child's Journey?
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: 'rgba(255,255,255,0.85)',
              fontWeight: 400,
              mb: 4,
              fontSize: { xs: '1rem', md: '1.2rem' },
            }}
          >
            Enroll now for 2027 classes. Limited slots available.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              component={RouterLink}
              to="/contact"
              sx={{
                bgcolor: '#fff',
                color: 'secondary.dark',
                fontWeight: 700,
                px: 5,
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.9)',
                },
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              }}
            >
              Enroll Now
            </Button>
            <Button
              variant="outlined"
              size="large"
              component={RouterLink}
              to="/schedule"
              sx={{
                borderColor: '#fff',
                color: '#fff',
                fontWeight: 700,
                px: 5,
                '&:hover': {
                  borderColor: '#fff',
                  bgcolor: 'rgba(255,255,255,0.15)',
                },
              }}
            >
              View Schedule
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default HomePage;
